import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import AdminLayout from '@/layouts/AdminLayout';
import MobileLayout from '@/layouts/MobileLayout';
import BuyerLayout from '@/layouts/BuyerLayout';
import LoadingSkeleton from '@/components/common/LoadingSkeleton';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import PermissionGate from '@/components/common/PermissionGate';
import PlatformGate from '@/components/common/PlatformGate';

/** 登录页（独立路由，不包 AdminLayout） */
const Login = lazy(() => import('@/pages/Login'));

/** Admin页面懒加载 */
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const ProductCategory = lazy(() => import('@/pages/admin/ProductCategory'));
const TradeOrders = lazy(() => import('@/pages/admin/TradeOrders'));
const FinanceReceivables = lazy(() => import('@/pages/admin/FinanceReceivables'));
const ContainerArchive = lazy(() => import('@/pages/admin/ContainerArchive'));
const ShippingBooking = lazy(() => import('@/pages/admin/ShippingBooking'));
const CustomsClearance = lazy(() => import('@/pages/admin/CustomsClearance'));
const DocumentVerification = lazy(() => import('@/pages/admin/DocumentVerification'));
const IoTAlerts = lazy(() => import('@/pages/admin/IoTAlerts'));
const ClaimRules = lazy(() => import('@/pages/admin/ClaimRules'));
const RiskRating = lazy(() => import('@/pages/admin/RiskRating'));
const BuyerManagement = lazy(() => import('@/pages/admin/BuyerManagement'));
const PortManage = lazy(() => import('@/pages/admin/PortManage'));
const RouteManage = lazy(() => import('@/pages/admin/RouteManage'));
const DutyRateManage = lazy(() => import('@/pages/admin/DutyRateManage'));
const CarrierManage = lazy(() => import('@/pages/admin/CarrierManage'));
const FreightManage = lazy(() => import('@/pages/admin/FreightManage'));
const NodeConfig = lazy(() => import('@/pages/admin/NodeConfig'));
const QuotationList = lazy(() => import('@/pages/admin/QuotationList'));
const SupplierManagement = lazy(() => import('@/pages/admin/SupplierManagement'));
const SupplierQuoteCompare = lazy(() => import('@/pages/admin/SupplierQuoteCompare'));
const ProductListing = lazy(() => import('@/pages/admin/ProductListing'));

/** 外部主体域（集采对外）：企业档案与认证审核 */
const ExtEnterpriseList = lazy(() => import('@/pages/admin/ExtEnterpriseList'));
const MiniappInternalAudit = lazy(() => import('@/pages/admin/MiniappInternalAudit'));

/** 货代储备台账（资源池主数据，供应商/货代议价与备用筹码） */
const ForwarderProfileList = lazy(() => import('@/pages/admin/ForwarderProfileList'));

/** 采购域（B3）：采购单 */
const PurchaseOrderList = lazy(() => import('@/pages/admin/PurchaseOrderList'));

/** 采购域（B5）：采购询价 RFQ / 比价 / 生成 PO */
const PurchaseInquiryList = lazy(() => import('@/pages/admin/PurchaseInquiryList'));
const PurchaseAllocationLedger = lazy(() => import('@/pages/admin/PurchaseAllocationLedger'));

/** 采购域（§7/§11#3）：质量验收模板主子表管理 */
const QualityTemplateList = lazy(() => import('@/pages/admin/QualityTemplateList'));

/** 采购域（B4）：收货单 */
const PurchaseReceiptList = lazy(() => import('@/pages/admin/PurchaseReceiptList'));

/** 溯源域（农残/原料）：农户主档 / 用药 / 三级检测 / EU 标准库 / 原料 / 订单溯源 */
const TraceFarmerList = lazy(() => import('@/pages/admin/TraceFarmerList'));
const TracePesticideList = lazy(() => import('@/pages/admin/TracePesticideList'));
const TraceDetectionList = lazy(() => import('@/pages/admin/TraceDetectionList'));
const TraceStandardList = lazy(() => import('@/pages/admin/TraceStandardList'));
const TraceMaterialList = lazy(() => import('@/pages/admin/TraceMaterialList'));
const TraceOrderReportList = lazy(() => import('@/pages/admin/TraceOrderReportList'));
const TraceWorkstationList = lazy(() => import('@/pages/admin/TraceWorkstationList'));
const TraceProcessingList = lazy(() => import('@/pages/admin/TraceProcessingList'));

/** 采购域（B4）：对账与付款 */
const PurchaseSettlementList = lazy(() => import('@/pages/admin/PurchaseSettlementList'));

/** 商品每日价格与价格趋势 */
const ProductPriceList = lazy(() => import('@/pages/admin/ProductPriceList'));const ProductPriceTrend = lazy(() => import('@/pages/admin/ProductPriceTrend'));
const ProductPriceConfig = lazy(() => import('@/pages/admin/ProductPriceConfig'));
const ProductPriceMaintenance = lazy(() => import('@/pages/admin/ProductPriceMaintenance'));

/** 汇率模块 */
const CurrencyConfig = lazy(() => import('@/pages/admin/CurrencyConfig'));
const CurrencyRateList = lazy(() => import('@/pages/admin/CurrencyRateList'));

/** 履约待办中心 */
const TodoCenter = lazy(() => import('@/pages/fulfillment/TodoCenter'));

/** T10 回款里程碑（T9 表单模板编辑器已收口进 NodeConfig，组件由 NodeConfig 直接复用） */
const ReceivableMilestone = lazy(() => import('@/pages/admin/ReceivableMilestone'));

/** IoT 四域页面懒加载 */
const IoTDevices = lazy(() => import('@/pages/admin/IoTDevices'));
const IoTDeviceShadow = lazy(() => import('@/pages/admin/IoTDeviceShadow'));
const IoTDeviceGroups = lazy(() => import('@/pages/admin/IoTDeviceGroups'));
const IoTDeviceTypes = lazy(() => import('@/pages/admin/IoTDeviceTypes'));
const IoTRealtime = lazy(() => import('@/pages/admin/IoTRealtime'));
const IoTHistory = lazy(() => import('@/pages/admin/IoTHistory'));
const IoTAlertRules = lazy(() => import('@/pages/admin/IoTAlertRules'));
const IoTNotifyConfig = lazy(() => import('@/pages/admin/IoTNotifyConfig'));
const IoTAIChat = lazy(() => import('@/pages/admin/IoTAIChat'));
const IoTAIKb = lazy(() => import('@/pages/admin/IoTAIKb'));
const DocumentReview = lazy(() => import('@/pages/admin/DocumentReview'));
const DataSourceConfig = lazy(() => import('@/pages/admin/DataSourceConfig'));
const AiLlmConfig = lazy(() => import('@/pages/admin/AiLlmConfig'));
const CustomerChat = lazy(() => import('@/pages/admin/CustomerChat'));
const InquiryTraining = lazy(() => import('@/components/admin/ai/InquiryEvalPanel'));
const AiEvalPlatform = lazy(() => import('@/pages/admin/AiEvalPlatform'));
const ExpertConsult = lazy(() => import('@/pages/admin/ExpertConsult'));
const AiRuntimeSwitch = lazy(() => import('@/pages/admin/AiRuntimeSwitch'));
const AiActionGate = lazy(() => import('@/pages/admin/AiActionGate'));
const AiQuoteDraftList = lazy(() => import('@/pages/admin/AiQuoteDraftList'));
const AiAgentRunList = lazy(() => import('@/pages/admin/AiAgentRunList'));
const AiIntentRouteConfig = lazy(() => import('@/pages/admin/AiIntentRouteConfig'));
const AiImageStorageList = lazy(() => import('@/pages/admin/AiImageStorageList'));
const IoTAnomalyDetection = lazy(() => import('@/pages/admin/IoTAnomalyDetection'));
const IoTDataPointTemplate = lazy(() => import('@/pages/admin/IoTDataPointTemplate'));
const IoTAlertCenter = lazy(() => import('@/pages/admin/IoTAlertCenter'));
const DeviceAccessConfig = lazy(() => import('@/pages/admin/DeviceAccessConfig'));

/** Mobile页面懒加载 */
const TaskPool = lazy(() => import('@/pages/mobile/TaskPool'));
const ScanBindIoT = lazy(() => import('@/pages/mobile/ScanBindIoT'));
const LoadingSeal = lazy(() => import('@/pages/mobile/LoadingSeal'));
const NodeInspection = lazy(() => import('@/pages/mobile/NodeInspection'));
const SignUnbox = lazy(() => import('@/pages/mobile/SignUnbox'));

/** Buyer页面懒加载 */
const OrderPayment = lazy(() => import('@/pages/buyer/OrderPayment'));
const FulfillmentTracking = lazy(() => import('@/pages/buyer/FulfillmentTracking'));
const DocumentReports = lazy(() => import('@/pages/buyer/DocumentReports'));
const ClaimCenter = lazy(() => import('@/pages/buyer/ClaimCenter'));

/** System页面懒加载 */
const SystemConfig = lazy(() => import('@/pages/system/SystemConfig'));
const RBACAudit = lazy(() => import('@/pages/system/RBACAudit'));
const AuditLogPage = lazy(() => import('@/pages/system/AuditLogPage'));
const AICopilot = lazy(() => import('@/pages/system/AICopilot'));
const PublicVerify = lazy(() => import('@/pages/system/PublicVerify'));
const UserManagement = lazy(() => import('@/pages/system/UserManagement'));
const RoleManagement = lazy(() => import('@/pages/system/RoleManagement'));
const MenuManagement = lazy(() => import('@/pages/system/MenuManagement'));
const OrgManagement = lazy(() => import('@/pages/system/OrgManagement'));
const DictConfig = lazy(() => import('@/pages/system/DictConfig'));
const BizConfigPage = lazy(() => import('@/pages/system/BizConfigPage'));
const EmailConfigPage = lazy(() => import('@/pages/system/EmailConfigPage'));
/** 个人设置（本人资料/头像/密码，登录即可访问，不挂 PermissionGate） */
const Profile = lazy(() => import('@/pages/system/Profile'));
const Forbidden = lazy(() => import('@/pages/Forbidden'));

/** P2 平台租户管理页面懒加载 */
const TenantList = lazy(() => import('@/pages/platform/TenantList'));
const TenantCreate = lazy(() => import('@/pages/platform/TenantCreate'));
const TenantDetail = lazy(() => import('@/pages/platform/TenantDetail'));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingSkeleton />}>{children}</Suspense>;
}

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LazyPage><Login /></LazyPage>,
  },
  {
    path: '/403',
    element: <LazyPage><Forbidden /></LazyPage>,
  },
  {
    path: '/',
    element: <ProtectedRoute><AdminLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <LazyPage><Dashboard /></LazyPage> },
      { path: 'admin/dashboard', element: <LazyPage><Dashboard /></LazyPage> },
      { path: 'admin/product-category', element: <LazyPage><PermissionGate perm="product:category:list"><ProductCategory /></PermissionGate></LazyPage> },
      { path: 'admin/trade-orders', element: <LazyPage><TradeOrders /></LazyPage> },
      { path: 'admin/finance-receivables', element: <LazyPage><FinanceReceivables /></LazyPage> },
      { path: 'admin/container-archive', element: <LazyPage><ContainerArchive /></LazyPage> },
      { path: 'admin/shipping-booking', element: <LazyPage><ShippingBooking /></LazyPage> },
      { path: 'admin/customs-clearance', element: <LazyPage><CustomsClearance /></LazyPage> },
      { path: 'admin/document-verification', element: <LazyPage><DocumentVerification /></LazyPage> },
      { path: 'admin/claim-rules', element: <LazyPage><ClaimRules /></LazyPage> },
      { path: 'admin/risk-rating', element: <LazyPage><RiskRating /></LazyPage> },
      { path: 'admin/buyer-management', element: <LazyPage><PermissionGate perm="buyer:list"><BuyerManagement /></PermissionGate></LazyPage> },
      { path: 'admin/port', element: <LazyPage><PermissionGate perm="port:list"><PortManage /></PermissionGate></LazyPage> },
      { path: 'admin/route', element: <LazyPage><PermissionGate perm="route:list"><RouteManage /></PermissionGate></LazyPage> },
      { path: 'admin/duty-rate', element: <LazyPage><PermissionGate perm="duty:list"><DutyRateManage /></PermissionGate></LazyPage> },
      { path: 'admin/carrier', element: <LazyPage><PermissionGate perm="carrier:list"><CarrierManage /></PermissionGate></LazyPage> },
      { path: 'admin/freight', element: <LazyPage><PermissionGate perm="freight:list"><FreightManage /></PermissionGate></LazyPage> },
      { path: 'admin/node-config', element: <LazyPage><PermissionGate perm="trade:task:list"><NodeConfig /></PermissionGate></LazyPage> },

      // ===== 报价管理（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/quotation', element: <LazyPage><PermissionGate perm="quotation:list"><QuotationList /></PermissionGate></LazyPage> },

      // ===== 供应商管理（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/supplier', element: <LazyPage><PermissionGate perm="supplier:list"><SupplierManagement /></PermissionGate></LazyPage> },

      // ===== 供应商报价比价（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/purchase-compare', element: <LazyPage><PermissionGate perm="supplier:quote:list"><SupplierQuoteCompare /></PermissionGate></LazyPage> },

      // ===== 采购域（B3）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/purchase-order', element: <LazyPage><PermissionGate perm="purchase:order:list"><PurchaseOrderList /></PermissionGate></LazyPage> },

      // ===== 采购询价（B5）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/purchase-inquiry', element: <LazyPage><PermissionGate perm="purchase:inquiry:list"><PurchaseInquiryList /></PermissionGate></LazyPage> },

      // ===== 采购占用台账 — path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/purchase-allocation', element: <LazyPage><PermissionGate perm="purchase:allocation:list"><PurchaseAllocationLedger /></PermissionGate></LazyPage> },

      // ===== 质量验收模板（§7/§11#3）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/quality-template', element: <LazyPage><PermissionGate perm="quality:template:list"><QualityTemplateList /></PermissionGate></LazyPage> },

      // ===== 收货管理（B4）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/purchase-receipt', element: <LazyPage><PermissionGate perm="purchase:receipt:list"><PurchaseReceiptList /></PermissionGate></LazyPage> },

      // ===== 对账与付款（B4）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/purchase-settlement', element: <LazyPage><PermissionGate perm="purchase:settlement:list"><PurchaseSettlementList /></PermissionGate></LazyPage> },

      // ===== 农残溯源域（农户 → 用药 → 检测 → 原料 → 订单）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/trace-farmer', element: <LazyPage><PermissionGate perm="trace:farmer:list"><TraceFarmerList /></PermissionGate></LazyPage> },
      { path: 'admin/trace-pesticide', element: <LazyPage><PermissionGate perm="trace:pesticide:list"><TracePesticideList /></PermissionGate></LazyPage> },
      { path: 'admin/trace-detection', element: <LazyPage><PermissionGate perm="trace:detection:list"><TraceDetectionList /></PermissionGate></LazyPage> },
      { path: 'admin/trace-standard', element: <LazyPage><PermissionGate perm="trace:standard:list"><TraceStandardList /></PermissionGate></LazyPage> },
      { path: 'admin/trace-material', element: <LazyPage><PermissionGate perm="trace:material:list"><TraceMaterialList /></PermissionGate></LazyPage> },
      { path: 'admin/trace-order', element: <LazyPage><PermissionGate perm="trace:order:list"><TraceOrderReportList /></PermissionGate></LazyPage> },
      { path: 'admin/trace-workstation', element: <LazyPage><PermissionGate perm="trace:workstation:list"><TraceWorkstationList /></PermissionGate></LazyPage> },
      { path: 'admin/trace-processing', element: <LazyPage><PermissionGate perm="trace:processing:list"><TraceProcessingList /></PermissionGate></LazyPage> },

      // ===== 外部主体域（集采对外）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/ext-enterprise', element: <LazyPage><PermissionGate perm="ext:enterprise:list"><ExtEnterpriseList /></PermissionGate></LazyPage> },
      { path: 'admin/miniapp-internal', element: <LazyPage><PermissionGate perm="miniapp:internal:audit"><MiniappInternalAudit /></PermissionGate></LazyPage> },

      // ===== 货代储备台账（资源池主数据）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/forwarder-profile', element: <LazyPage><PermissionGate perm="forwarder:profile:list"><ForwarderProfileList /></PermissionGate></LazyPage> },

      // ===== 商品上架（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/product-listing', element: <LazyPage><PermissionGate perm="product:listing:list"><ProductListing /></PermissionGate></LazyPage> },

      // ===== 履约 T5/T6（PermissionGate 权限门控）=====
      { path: 'admin/fulfillment/todo', element: <LazyPage><PermissionGate perm="trade:task:list"><TodoCenter /></PermissionGate></LazyPage> },

      // ===== 履约 T10（回款里程碑；T9 表单模板编辑器已收口进 NodeConfig）=====
      { path: 'admin/receivable-milestone', element: <LazyPage><PermissionGate perm="trade:task:list"><ReceivableMilestone /></PermissionGate></LazyPage> },

      // ===== 商品每日价格与价格趋势（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/product-price-list', element: <LazyPage><PermissionGate perm="product:price:list"><ProductPriceList /></PermissionGate></LazyPage> },
      { path: 'admin/product-price-trend', element: <LazyPage><PermissionGate perm="product:price:trend"><ProductPriceTrend /></PermissionGate></LazyPage> },
      { path: 'admin/product-price-config', element: <LazyPage><PermissionGate perm="product:price:config"><ProductPriceConfig /></PermissionGate></LazyPage> },
      // 每日价格维护（批量确认行情价；与小程序工作台共用后端，复用 product:price:list 权限）
      { path: 'admin/product-price-maintenance', element: <LazyPage><PermissionGate perm="product:price:list"><ProductPriceMaintenance /></PermissionGate></LazyPage> },

      // ===== 汇率模块（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/currency-config', element: <LazyPage><PermissionGate perm="currency:config:list"><CurrencyConfig /></PermissionGate></LazyPage> },
      { path: 'admin/currency-rate-list', element: <LazyPage><PermissionGate perm="currency:rate:list"><CurrencyRateList /></PermissionGate></LazyPage> },

      // ===== IoT 四域路由（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      // 设备域
      { path: 'admin/iot-device-ledger', element: <LazyPage><PermissionGate perm="iot:device:list"><IoTDevices /></PermissionGate></LazyPage> },
      // 设备影子：不再作为独立侧边栏菜单，改由设备台账操作列「查看影子」携带 id 进入
      { path: 'admin/iot-device-shadow/:id', element: <LazyPage><PermissionGate perm="iot:device:list"><IoTDeviceShadow /></PermissionGate></LazyPage> },
      { path: 'admin/iot-datapoint-tpl', element: <LazyPage><PermissionGate perm="iot:device:template"><IoTDataPointTemplate /></PermissionGate></LazyPage> },
      { path: 'admin/iot-device-type', element: <LazyPage><PermissionGate perm="iot:device:type:list"><IoTDeviceTypes /></PermissionGate></LazyPage> },
      { path: 'admin/iot-device-group', element: <LazyPage><PermissionGate perm="iot:device:group:list"><IoTDeviceGroups /></PermissionGate></LazyPage> },
      // 数据域
      { path: 'admin/iot-realtime', element: <LazyPage><PermissionGate perm="iot:data:view"><IoTRealtime /></PermissionGate></LazyPage> },
      { path: 'admin/iot-history', element: <LazyPage><PermissionGate perm="iot:data:history"><IoTHistory /></PermissionGate></LazyPage> },
      // 告警域
      { path: 'admin/iot-alerts', element: <LazyPage><PermissionGate perm="iot:alert:list"><IoTAlerts /></PermissionGate></LazyPage> },
      { path: 'admin/iot-alert-center', element: <LazyPage><PermissionGate perm="iot:alert:list"><IoTAlertCenter /></PermissionGate></LazyPage> },
      { path: 'admin/iot-alert-rule', element: <LazyPage><PermissionGate perm="iot:alert:rule:list"><IoTAlertRules /></PermissionGate></LazyPage> },
      { path: 'admin/iot-notify', element: <LazyPage><PermissionGate perm="iot:alert:notify:list"><IoTNotifyConfig /></PermissionGate></LazyPage> },
      // AI 域
      { path: 'admin/iot-ai-chat', element: <LazyPage><PermissionGate perm="iot:ai:chat"><IoTAIChat /></PermissionGate></LazyPage> },
      { path: 'admin/iot-ai-kb', element: <LazyPage><PermissionGate perm="iot:ai:kb:list"><IoTAIKb /></PermissionGate></LazyPage> },
      { path: 'admin/iot-ai-kb/review', element: <LazyPage><PermissionGate perm="iot:ai:kb:list"><DocumentReview /></PermissionGate></LazyPage> },
      { path: 'admin/iot-ai-kb/sources', element: <LazyPage><PermissionGate perm="iot:ai:kb:list"><DataSourceConfig /></PermissionGate></LazyPage> },
      // AI 模型配置（C4 动态模型热切换）— path & perm 与后端 sys_menu 种子保持一致
      { path: 'admin/ai-llm-config', element: <LazyPage><PermissionGate perm="iot:ai:chat"><AiLlmConfig /></PermissionGate></LazyPage> },
      { path: 'admin/customer-chat', element: <LazyPage><PermissionGate perm="customer:chat:list"><CustomerChat /></PermissionGate></LazyPage> },
      // AI 评测平台（B4 · U-54~U-58：出题/三档验证/评审/看板）
      { path: 'admin/ai-inquiry-training', element: <LazyPage><PermissionGate perm="iot:ai:chat"><InquiryTraining /></PermissionGate></LazyPage> },
      { path: 'admin/ai-eval', element: <LazyPage><PermissionGate perm="iot:ai:chat"><AiEvalPlatform /></PermissionGate></LazyPage> },
      // AI 专家会诊（B5 · U-02：E1 冷链/IoT、E2 贸易/履约、E3 理赔/合规 三领域顾问）
      { path: 'admin/ai-expert', element: <LazyPage><PermissionGate perm="iot:ai:chat"><ExpertConsult /></PermissionGate></LazyPage> },
      // AI 运行开关（页面热切换 scheduled/reranker，免重启）
      { path: 'admin/ai-runtime-switch', element: <LazyPage><PermissionGate perm="iot:ai:chat"><AiRuntimeSwitch /></PermissionGate></LazyPage> },
      // AI 动作闸门（P0 可追溯：X 级动作人工确认/驳回，权限 iot:ai:action）
      { path: 'admin/ai-action-gate', element: <LazyPage><PermissionGate perm="iot:ai:action"><AiActionGate /></PermissionGate></LazyPage> },
      // AI 报价草稿（② 可追溯：W5 落库 DRAFT）
      { path: 'admin/ai-quote-draft', element: <LazyPage><PermissionGate perm="iot:ai:quote"><AiQuoteDraftList /></PermissionGate></LazyPage> },
      // AI 编排运行（③ 可追溯：Plan 模板 + Run 委派审计）
      { path: 'admin/ai-agent-run', element: <LazyPage><PermissionGate perm="iot:ai:agent"><AiAgentRunList /></PermissionGate></LazyPage> },
      // AI 意图路由（④ 可追溯：规则配置/查看，运营免重启）
      { path: 'admin/ai-intent-route', element: <LazyPage><PermissionGate perm="iot:ai:route"><AiIntentRouteConfig /></PermissionGate></LazyPage> },
      // AI 图片存储（⑥ 可追溯：MinIO 真实对象查看）
      { path: 'admin/ai-image-storage', element: <LazyPage><PermissionGate perm="iot:ai:image"><AiImageStorageList /></PermissionGate></LazyPage> },
      // 异常检测
      { path: 'admin/iot-anomaly', element: <LazyPage><PermissionGate perm="iot:device:anomaly:list"><IoTAnomalyDetection /></PermissionGate></LazyPage> },
      // 配置驱动接入框架（解码脚本 + 连接器实例）— 不加 PermissionGate，避免权限拦截
      { path: 'admin/iot-device-access', element: <LazyPage><DeviceAccessConfig /></LazyPage> },

      { path: 'system/config', element: <LazyPage><SystemConfig /></LazyPage> },
      // 个人设置（与顶部右上角「个人设置」入口一致；本人数据，无需权限码）
      { path: 'system/profile', element: <LazyPage><Profile /></LazyPage> },
      { path: 'system/dict', element: <LazyPage><DictConfig /></LazyPage> },
      // 业务参数配置（biz_config 租户级维护）— path & perm 与后端 sys_menu 种子保持一致
      { path: 'system/biz-config', element: <LazyPage><PermissionGate perm="system:bizconfig:list"><BizConfigPage /></PermissionGate></LazyPage> },
      // 平台级邮件配置（system:emailconfig:list 门控）— path & perm 与后端 sys_menu 种子保持一致
      { path: 'system/email-config', element: <LazyPage><PermissionGate perm="system:emailconfig:list"><EmailConfigPage /></PermissionGate></LazyPage> },
      { path: 'system/rbac-audit', element: <LazyPage><RBACAudit /></LazyPage> },
      { path: 'system/audit-log', element: <LazyPage><PermissionGate perm="system:audit:list"><AuditLogPage /></PermissionGate></LazyPage> },
      { path: 'system/ai-copilot', element: <LazyPage><AICopilot /></LazyPage> },
      { path: 'system/public-verify', element: <LazyPage><PublicVerify /></LazyPage> },
      // ===== RBAC 4 模块（权限网关包裹） =====
      { path: 'system/user', element: <LazyPage><PermissionGate perm="system:user:list"><UserManagement /></PermissionGate></LazyPage> },
      { path: 'system/role', element: <LazyPage><PermissionGate perm="system:role:list"><RoleManagement /></PermissionGate></LazyPage> },
      { path: 'system/menu', element: <LazyPage><PermissionGate perm="system:menu:list"><MenuManagement /></PermissionGate></LazyPage> },
      { path: 'system/org', element: <LazyPage><PermissionGate perm="system:org:list"><OrgManagement /></PermissionGate></LazyPage> },
      // ===== P2 平台管理（仅平台超管，PlatformGate 门控）=====
      { path: 'platform/tenants', element: <LazyPage><PlatformGate><TenantList /></PlatformGate></LazyPage> },
      { path: 'platform/tenants/new', element: <LazyPage><PlatformGate><TenantCreate /></PlatformGate></LazyPage> },
      { path: 'platform/tenants/:orgCode', element: <LazyPage><PlatformGate><TenantDetail /></PlatformGate></LazyPage> },
    ],
  },
  {
    path: '/mobile',
    element: <MobileLayout />,
    children: [
      { index: true, element: <LazyPage><TaskPool /></LazyPage> },
      { path: 'tasks', element: <LazyPage><TaskPool /></LazyPage> },
      { path: 'scan', element: <LazyPage><PermissionGate perm="iot:device:bind"><ScanBindIoT /></PermissionGate></LazyPage> },
      { path: 'loading-seal', element: <LazyPage><LoadingSeal /></LazyPage> },
      { path: 'inspection', element: <LazyPage><NodeInspection /></LazyPage> },
      { path: 'sign-unbox', element: <LazyPage><SignUnbox /></LazyPage> },
    ],
  },
  {
    path: '/buyer',
    element: <BuyerLayout />,
    children: [
      { index: true, element: <LazyPage><OrderPayment /></LazyPage> },
      { path: 'payment', element: <LazyPage><OrderPayment /></LazyPage> },
      { path: 'tracking', element: <LazyPage><FulfillmentTracking /></LazyPage> },
      { path: 'documents', element: <LazyPage><DocumentReports /></LazyPage> },
      { path: 'claims', element: <LazyPage><ClaimCenter /></LazyPage> },
    ],
  },
];
